<template>
  <v-dialog :model-value="!!request" @update:model-value="$emit('close')" max-width="800px">
    <v-card v-if="request">
      <v-card-title class="d-flex align-center justify-space-between">
        <span class="text-h5">Request Details</span>
        <v-btn icon="mdi-close" variant="text" @click="$emit('close')"></v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-6">
        <!-- Student Information Section -->
        <div class="mb-6">
          <div class="text-h6 mb-3">Student Information</div>
          <v-row>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">Name</div>
              <div class="text-body-1">{{ request.student_name }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">School</div>
              <div class="text-body-1">{{ request.school_name || request.school }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">Grade</div>
              <div class="text-body-1">{{ request.grade_level || 'N/A' }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">Contact Email</div>
              <div class="text-body-1">
                <a v-if="request.contact_email" :href="`mailto:${request.contact_email}`" class="text-primary text-decoration-none">
                  {{ request.contact_email }}
                </a>
                <span v-else>N/A</span>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">Contact Phone</div>
              <div class="text-body-1">{{ request.contact_phone || 'N/A' }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">Teacher's Email</div>
              <div class="text-body-1">
                <a v-if="request.teacher_email" :href="`mailto:${request.teacher_email}`" class="text-primary text-decoration-none">
                  {{ request.teacher_email }}
                </a>
                <span v-else>N/A</span>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">Assignee</div>
              <div class="text-body-1">{{ request.assignee_name || 'None' }}</div>
            </v-col>
            <v-col v-if="request.technology_access" cols="12">
              <div class="text-caption text-medium-emphasis">Current Access to Technology at Home</div>
              <div class="text-body-1">{{ request.technology_access }}</div>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-4"></v-divider>

        <!-- Equipment Request Section -->
        <div class="mb-6">
          <div class="text-h6 mb-3">Equipment Request</div>
          <v-row>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">Equipment Type</div>
              <div class="text-body-1">{{ formatEquipmentType(request.equipment_type) }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">Minimum Rating</div>
              <v-chip color="primary" size="small" class="mt-1">
                {{ request.minimum_rating || 'N/A' }}
              </v-chip>
            </v-col>
            <v-col cols="12">
              <div class="text-caption text-medium-emphasis mb-2">Needed For (Tasks)</div>
              <div class="d-flex flex-wrap ga-2">
                <v-chip v-for="task in request.needed_for" :key="task" size="small" variant="tonal">
                  {{ formatTask(task) }}
                </v-chip>
              </div>
            </v-col>
            <v-col v-if="request.work_description" cols="12">
              <div class="text-caption text-medium-emphasis">Work Description</div>
              <div class="text-body-1">{{ request.work_description }}</div>
            </v-col>
            <v-col v-if="request.additional_comments" cols="12">
              <div class="text-caption text-medium-emphasis">Additional Comments</div>
              <div class="text-body-1">{{ request.additional_comments }}</div>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-4"></v-divider>

        <!-- Status Section -->
        <div>
          <div class="text-h6 mb-3">Status</div>
          <v-row>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">Current Status</div>
              <v-chip :color="getStatusColor(request.status)" size="small" class="mt-1">
                {{ formatStatus(request.status) }}
              </v-chip>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">Submitted</div>
              <div class="text-body-1">{{ formatDateTime(request.submitted_at) }}</div>
            </v-col>
            <v-col v-if="request.assigned_asset_tag" cols="12">
              <div class="text-caption text-medium-emphasis">Assigned Equipment</div>
              <div class="text-body-1 font-weight-bold">{{ request.assigned_asset_tag }}</div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          v-if="request.status === 'pending' && !request.assigned_equipment_id"
          color="primary"
          variant="flat"
          @click="$emit('fill-request', request)"
        >
          Fill Request
        </v-btn>
        <v-btn
          v-if="request.status === 'filled'"
          color="success"
          variant="flat"
          @click="$emit('mark-delivered', request)"
        >
          <v-icon start>mdi-truck-delivery</v-icon>
          Deliver
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
defineProps<{
  request: any | null
}>()

defineEmits<{
  'close': []
  'fill-request': [request: any]
  'mark-delivered': [request: any]
}>()

const formatEquipmentType = (type: string) => {
  if (!type) return 'Unknown'
  return type.charAt(0).toUpperCase() + type.slice(1)
}

const formatStatus = (status: string) => {
  if (!status) return 'Unknown'
  return status.split('_').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ')
}

const formatTask = (task: string) => {
  if (!task) return ''
  return task.split('_').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ')
}

const formatDateTime = (date: string) => {
  if (!date) return 'N/A'
  return new Date(date).toLocaleString()
}

const getStatusColor = (status: string) => {
  const statusColors: Record<string, string> = {
    pending: 'warning',
    under_review: 'info',
    approved: 'success',
    fulfilled: 'success',
    filled: 'info',
    completed: 'success',
    delivered: 'success',
    cancelled: 'error',
    denied: 'error'
  }
  return statusColors[status] || 'grey'
}
</script>
